import { Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { catchError, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { collection, doc, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { UserManagementService } from 'src/app/services/user-management.service';
import { AuthService } from '@core/auth.service';
import { CatchErrorService } from '@core/catch-error.service';

@Injectable({
  providedIn: 'root'
})
export class GameRecommendationService {

    constructor(
        private userManagementService: UserManagementService,
        private authService: AuthService,
        private catchService: CatchErrorService,
        private firestore: Firestore,
    ) {}

    recommendationsGames$ = new BehaviorSubject([]);
    yourToPlayGame$ = new BehaviorSubject({});

    recommendations$ = this.userManagementService?.userRequest$.pipe(
    switchMap((user) => {
      if (!user) return of([]);

      return this.authService.getUid$('GamesRepositoryService').pipe(
        switchMap((userId) => {
          if (!userId) return of([]);
          return this.authService
            .getAngularFireStore(
              `/users/${userId}/game/recommendation`,
              'snapshotChanges'
            )
            .pipe(
              map((res: Data) => res?.payload?.data()),
              switchMap((packageName: Data) => {
                if (!packageName?.package_name) {
                  return this.findDefault();
                }
                return this.authService
                  .getAngularFireStore(
                    `/general_modules/game/recommendation/${packageName.package_name}`,
                    'snapshotChanges'
                  )
                  .pipe(
                    map((res: Data) => res?.payload?.data()),
                    switchMap((recommended: Data) => {
                      if (!recommended?.details?.length) {
                        return of([]);
                      }

                      // Convert all onSnapshot calls into Observables
                      const detailStreams = recommended.details
                        .filter((d) => d?.game_ref)
                        .map((d) =>
                          new Observable((observer) => {
                            const unsubscribe = d.game_ref.onSnapshot((ref) => {
                              observer.next({
                                ...d,
                                gameDetails: ref?.data(),
                              });
                            });

                            return () => unsubscribe();
                          })
                        );

                      return combineLatest(detailStreams);
                    }),
                    tap((details: Data[]) => {
                      this.recommendationsGames$.next(details);
                    }),
                    catchError(() => of([]))
                  );
              })
            );
        })
      );
    })
    );

  findDefault(): any {
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of([]);

          // Modular Firestore API: Reference the collection
          const recommendationCollectionRef = collection(
            this.firestore,
            `/general_modules/game/recommendation`
          );

          // Use onSnapshot for real-time updates
          return new Observable((subscriber) => {
            const unsubscribe = onSnapshot(
              recommendationCollectionRef,
              (snapshot) => {
                const actions = snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                }));
                subscriber.next(actions);
              },
              () => subscriber.next([])
            );
            return () => unsubscribe();
          }).pipe(
            this.catchService.retryWhen,
            this.catchService.catchOfflineError
          );
        }),
        map((data: Data) => data.find((d: Data) => d?.is_default)?.details ?? []),
        switchMap((details) => {
          if (!details.length) {
            return of([]);
          }

          const detailStreams = details
            .filter((d) => d?.game_ref)
            .map(
              (d) =>
                new Observable((observer) => {
                  const unsubscribe = d.game_ref.onSnapshot((ref: Data) => {
                    observer.next({
                      ...d,
                      gameDetails: ref?.data(),
                    });
                  });

                  return () => unsubscribe();
                })
            );

          return combineLatest(detailStreams);
        }),
        tap((details: Data[]) => {
          this.recommendationsGames$.next(details);
        }),
        catchError(() => of([])),
        shareReplay(1)
      );
  }

  getGamePlayLastSession() {
    this.authService.getUid$("GamesRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.authService
          .getAngularFireStore(`/users/${userId}/game/recommendation`, "snapshotChanges")
          .pipe(map((res: Data) => res?.payload?.data()));
      })
    )?.subscribe(
      (data) => {
        this.getGameRecommendation(data);
      },
      (err) => {
        return err;
      }
    );
  }

  getGameRecommendation(gameId: Data) {
      this.catchService.isOnline$
        .pipe(
          switchMap((isOnline) => {
            if (!isOnline) return of(null);
  
            // Modular Firestore API: Reference the document
            const recommendationDocRef = doc(
              this.firestore,
              `/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation/${gameId && gameId.parent_game_id ? String(gameId.parent_game_id) : "1"}`
            );
  
            return new Observable((subscriber) => {
              const unsubscribe = onSnapshot(
                recommendationDocRef,
                (snapshot) => {
                  subscriber.next(snapshot.data());
                },
                (error) => {
                  subscriber.error(error);
                }
              );
  
              return () => unsubscribe();
            }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        )
        .pipe(map((res: Data) => res))
        ?.subscribe(async (data: Data) => {
          if (!data) {
            this.catchService.isOnline$
              .pipe(
                switchMap((isOnline) => {
                  if (!isOnline) return of(null);
  
                  // Modular Firestore API: Reference the fallback document
                  const fallbackDocRef = doc(
                    this.firestore,
                    `/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation/1`
                  );
  
                  return new Observable((subscriber) => {
                    const unsubscribe = onSnapshot(
                      fallbackDocRef,
                      (snapshot) => {
                        subscriber.next(snapshot.data());
                      },
                      (error) => {
                        subscriber.error(error);
                      }
                    );
  
                    return () => unsubscribe();
                  }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
                }),
                this.catchService.retryWhen,
                this.catchService.catchOfflineError,
                shareReplay(1)
              )
              .pipe(map((res: Data) => res))
              ?.subscribe(
                async (d: Data) => {
                  if (!d || !d.detail) return;
                  for (let g of d.detail) {
                    const gameData = await this.getData(g);
                    g["gameData"] = gameData;
                  }
                  const finalData = d?.detail;
                  this.yourToPlayGame$.next(finalData);
                  return;
                },
                (err) => {
                  return err;
                }
              );
          }
          if (!data || !data.detail) return;
          for (let g of data.detail) {
            const gameData = await this.getData(g);
            g["gameData"] = gameData;
          }
          const finalData = data?.detail;
          this.yourToPlayGame$.next(finalData);
        });
    }

    async getData(gRef: Data): Promise<any> {
        const snap = await getDoc(gRef.game_ref);
        return snap.data();
    }
}